'use client'

import { useCallback, useRef } from 'react'
import { toast } from 'sonner'

// ---------------------------------------------------------------------------
// Shortcut map: nav item id -> keyboard shortcut label
// ---------------------------------------------------------------------------

const SHORTCUT_MAP: Record<string, { shortcut: string; label: string }> = {
  today: { shortcut: 'Cmd+1', label: 'Today' },
  upcoming: { shortcut: 'Cmd+2', label: 'Upcoming' },
  inbox: { shortcut: 'Cmd+3', label: 'Inbox' },
  projects: { shortcut: 'Cmd+4', label: 'Projects' },
  journal: { shortcut: 'Cmd+5', label: 'Journal' },
}

const STORAGE_KEY = 'konstantout-shortcut-hints-shown'

function getShownHints(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return new Set()
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed)) return new Set(parsed as string[])
    return new Set()
  } catch {
    return new Set()
  }
}

function markHintShown(itemId: string): void {
  const shown = getShownHints()
  shown.add(itemId)
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...shown]))
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useShortcutDiscovery() {
  // Track shown hints in a ref to avoid re-reads within the same session
  const sessionShownRef = useRef<Set<string>>(new Set())

  const showHint = useCallback((itemId: string) => {
    const mapping = SHORTCUT_MAP[itemId]
    if (!mapping) return

    // Skip if already shown this session or in localStorage
    if (sessionShownRef.current.has(itemId)) return
    const persisted = getShownHints()
    if (persisted.has(itemId)) {
      sessionShownRef.current.add(itemId)
      return
    }

    // Mark as shown
    sessionShownRef.current.add(itemId)
    markHintShown(itemId)

    // Show toast
    toast(`Tip: ${mapping.shortcut} goes to ${mapping.label}`, {
      duration: 3000,
      position: 'bottom-right',
      style: {
        fontSize: '12px',
        padding: '8px 12px',
        background: 'var(--muted)',
        color: 'var(--muted-foreground)',
        border: '1px solid var(--border)',
      },
    })
  }, [])

  return { showHint }
}
